import type { SupportedLanguages } from '../i18n';
import { logger } from '../lib/logger';

// Liste des langues supportées
const supportedLanguages: SupportedLanguages[] = ['fr', 'en', 'es', 'it', 'pt', 'de', 'nl', 'ja', 'zh', 'ru'];

// Fonction pour normaliser le code de langue
const normalizeLanguageCode = (code: string): string => {
  // Extraire la partie principale du code de langue (ex: 'fr-FR' -> 'fr')
  return code.toLowerCase().split(/[-_]/)[0];
};

// Fonction pour vérifier si une langue est supportée
const isSupportedLanguage = (code: string): boolean => {
  const normalizedCode = normalizeLanguageCode(code);
  return supportedLanguages.includes(normalizedCode as SupportedLanguages);
};

// Fonction pour obtenir la langue du navigateur avec détection améliorée
export const getBrowserLanguage = (): SupportedLanguages => {
  const languageSources = [
    // Langue principale du navigateur
    navigator.language,
    // Liste des langues préférées
    ...(navigator.languages || []),
    // User Agent (pour les anciens navigateurs)
    navigator.userAgent?.match(/([a-z]{2})-?/)?.[1],
    // Accept-Language header (si disponible)
    document.documentElement.lang
  ].filter(Boolean);

  // Créer un map de scores pour chaque langue
  const languageScores = new Map<string, number>();

  languageSources.forEach((source, index) => {
    const normalizedCode = normalizeLanguageCode(source);
    if (isSupportedLanguage(normalizedCode)) {
      // Donner plus de poids aux premières langues de la liste
      const score = 1 / (index + 1);
      languageScores.set(normalizedCode, (languageScores.get(normalizedCode) || 0) + score);
    }
  });

  // Trouver la langue avec le meilleur score
  let bestLanguage: string = 'fr';
  let bestScore = 0;

  languageScores.forEach((score, language) => {
    if (score > bestScore) {
      bestScore = score;
      bestLanguage = language;
    }
  });

  return bestLanguage as SupportedLanguages;
};

// Fonction pour obtenir la langue depuis localStorage avec validation
export const getStoredLanguage = (): SupportedLanguages | null => {
  try {
    const stored = localStorage.getItem('i18nextLng');
    if (stored && isSupportedLanguage(stored)) {
      const normalized = normalizeLanguageCode(stored);
      return normalized as SupportedLanguages;
    }
  } catch (error) {
    logger.error('Error accessing localStorage', {
      category: 'Language',
      data: { error }
    });
  }
  return null;
};

// Fonction pour sauvegarder la langue dans localStorage avec validation
export const storeLanguage = (language: SupportedLanguages): void => {
  try {
    if (!isSupportedLanguage(language)) {
      throw new Error(`Unsupported language: ${language}`);
    }
    
    // Store the normalized language code
    const normalizedLanguage = normalizeLanguageCode(language);
    localStorage.setItem('i18nextLng', normalizedLanguage);
  } catch (error) {
    logger.error('Error storing language', {
      category: 'Language',
      data: { error, language }
    });
  }
};

// Fonction principale pour détecter la langue avec fallback progressif
export const detectLanguage = (): SupportedLanguages => {
  try {
    // 1. Essayer d'abord de récupérer depuis localStorage
    const storedLanguage = getStoredLanguage();
    if (storedLanguage) {
      return storedLanguage;
    }

    // 2. Utiliser la langue du navigateur
    const browserLanguage = getBrowserLanguage();
    
    // 3. Sauvegarder la langue détectée
    storeLanguage(browserLanguage);
    
    return browserLanguage;
  } catch (error) {
    logger.error('Language detection failed', {
      category: 'Language',
      data: { error }
    });
    
    // Fallback ultime sur le français
    return 'fr';
  }
};

// Fonction pour obtenir la langue la plus proche disponible
export const getNearestLanguage = (language: string): SupportedLanguages => {
  const normalized = normalizeLanguageCode(language);

  // Si la langue est déjà supportée
  if (isSupportedLanguage(normalized)) {
    return normalized as SupportedLanguages;
  }

  // Correspondances linguistiques proches
  const languageMap: Record<string, SupportedLanguages> = {
    'ca': 'es', // Catalan -> Espagnol
    'gl': 'es', // Galicien -> Espagnol
    'eu': 'es', // Basque -> Espagnol
    'oc': 'fr', // Occitan -> Français
    'co': 'fr', // Corse -> Français
    'br': 'fr', // Breton -> Français
    'gsw': 'de', // Suisse allemand -> Allemand
    'nds': 'de', // Bas allemand -> Allemand
    'yue': 'zh', // Cantonais -> Chinois
    'wuu': 'zh', // Wu -> Chinois
    'cmn': 'zh', // Mandarin -> Chinois
  };

  // Vérifier les correspondances directes
  if (normalized in languageMap) {
    return languageMap[normalized];
  }

  // Vérifier la famille linguistique
  const languageFamilies: Record<string, SupportedLanguages[]> = {
    'romance': ['fr', 'es', 'it', 'pt'],
    'germanic': ['de', 'nl', 'en'],
    'asian': ['ja', 'zh'],
    'slavic': ['ru']
  };

  for (const [family, languages] of Object.entries(languageFamilies)) {
    if (languages.includes(normalized as SupportedLanguages)) {
      return languages[0];
    }
  }

  // Fallback sur l'anglais comme langue internationale
  return 'en';
};